import { Box, Typography } from '@mui/material';
import { useObjectDetailsDialog } from '~/components/Properties/ObjectDetailsDialog';
import { ObjectRow } from '~/components/Properties/ObjectInfoButton';
import PropertiesTooltip from '~/components/Properties/PropertiesTooltip';
import StructureSetProperties from '~/components/Properties/StructureSetProperties';
import SelectableListItem from '~/components/SelectableListItem/SelectableListItem';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import StructureSetInfoButton from '~/components/Structures/StructureSetInfoButton';
import { AvailableStructureSetView } from '~/domain/projections/views';
import { useStructureSetDetailView } from '~/hooks/domain/useStructureSetDetailView';
import { cutUID } from '~/utils/string';

type StructureSetItemProps = {
    structureSet: AvailableStructureSetView;
    isActive?: boolean;
    /**
     * Let a click on the active row select it again. Selecting the open set
     * once more re-binds the viewer to that set's own image, so a list whose
     * rows offer that keeps the active row a target.
     */
    allowReselect?: boolean;
    onClick: () => void;
    onDelete?: () => void;
};

export default function StructureSetItem({
    structureSet,
    isActive = false,
    allowReselect = false,
    onClick,
    onDelete,
}: StructureSetItemProps) {
    const { displayName, sopInstanceUid, structureSetId } = structureSet;
    const detail = useStructureSetDetailView(structureSetId);
    const dialog = useObjectDetailsDialog();

    return (
        <SelectableListItem
            isSelected={isActive}
            // The active set is not a selection target unless the list says
            // otherwise, but its row stays interactive either way: a disabled
            // row blocks the pointer for the details button too, on the set
            // whose details are wanted most.
            onClick={isActive && !allowReselect ? undefined : onClick}
            data-testid={`structureset-menu-item-${displayName}`}
        >
            <ObjectRow
                info={
                    <StructureSetInfoButton
                        structureSet={structureSet}
                        dialog={dialog}
                        onDelete={onDelete}
                    />
                }
            >
                <PropertiesTooltip
                    title={
                        <StructureSetProperties
                            structureSet={structureSet}
                            onViewDicom={
                                structureSet.isLoaded
                                    ? () => dialog.open('tags')
                                    : undefined
                            }
                            onDelete={onDelete}
                        />
                    }
                >
                    <Box width="100%" overflow="hidden" display="grid">
                        <Box
                            alignItems="center"
                            display="grid"
                            gridTemplateColumns="1fr auto"
                            gap={1}
                        >
                            <Typography
                                color={
                                    isActive || isActive === undefined
                                        ? 'text.primary'
                                        : 'text.secondary'
                                }
                                fontSize={12}
                                noWrap
                            >
                                <SensitiveInfoTypography>
                                    {displayName}
                                </SensitiveInfoTypography>
                            </Typography>
                            {detail && (
                                <Typography
                                    fontSize={10}
                                    color="text.secondary"
                                    flexShrink={0}
                                >
                                    {detail.numberOfStructures} ROIs
                                </Typography>
                            )}
                        </Box>
                        <Box
                            display="grid"
                            gridTemplateColumns="1fr auto"
                            alignItems="center"
                            gap={1}
                        >
                            <Typography
                                fontSize={10}
                                color="text.secondary"
                                noWrap
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                <SensitiveInfoTypography>
                                    {cutUID(sopInstanceUid, 4, 4)}
                                </SensitiveInfoTypography>
                            </Typography>
                            {structureSet.imageLabel && (
                                <Typography
                                    fontSize={10}
                                    color="text.secondary"
                                    noWrap
                                >
                                    <SensitiveInfoTypography>
                                        {structureSet.imageLabel}
                                    </SensitiveInfoTypography>
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </PropertiesTooltip>
            </ObjectRow>
        </SelectableListItem>
    );
}
